/*
 * GEMIANS — Kaching Bundles adapter for the ATC footer
 *
 * Feeds Gemians.atcFooter from the Kaching Bundles widget. Mapping was read off
 * the live widget on the test product, 14 Aug 2026:
 *
 *   tier row            .kaching-bundles__bar          (+ --selected)
 *   tier price          .kaching-bundles__bar-price          "$56.00"
 *   tier compare-at     .kaching-bundles__bar-full-price     "$141.98"
 *   tier saving         .kaching-bundles__bar-subtitle       "You save $85.98"
 *   subscribe card      .kaching-bundles__subscriptions__card
 *   selling plan        form[action*="/cart/add"] input[name="selling_plan"]
 *
 * Money is taken as the widget already printed it. Nothing here parses a
 * number or builds a string, so every market stays correct by construction.
 */
(function () {
  'use strict';

  if (!window.Gemians || !window.Gemians.atcFooter) return;

  var CART_FORM = 'form[action*="/cart/add"]';

  function clean(el) {
    var value = el && el.textContent ? el.textContent.trim() : '';
    return value || null;
  }

  // The subscription card reuses .kaching-bundles__bar-container, so a plain
  // ".kaching-bundles__bar--selected" lookup can land on it. Requiring a price
  // node inside is what separates a real tier from the subscription card.
  function selectedTier() {
    var bars = document.querySelectorAll('.kaching-bundles__bar--selected');
    for (var i = 0; i < bars.length; i++) {
      if (bars[i].querySelector('.kaching-bundles__bar-price')) return bars[i];
    }
    return null;
  }

  function fromTier(selector) {
    var tier = selectedTier();
    return tier ? clean(tier.querySelector(selector)) : null;
  }

  // The theme prints two mutually exclusive price branches and shows only one:
  // .f-price__sale while a compare-at price exists, .f-price__regular otherwise.
  // Both branches contain a .f-price-item--regular, and the app updates the
  // first one — which on a discounted product is the hidden branch, so the
  // shopper kept seeing the variant's own price while a different tier was
  // selected. Resolve the node that is actually on screen instead of trusting
  // either class name on its own.
  function visiblePriceNode() {
    var wrap = document.querySelector('product-info [id^="price-"]');
    if (!wrap) return null;
    var branches = [
      ['.f-price__sale', '.f-price-item--sale'],
      ['.f-price__regular', '.f-price-item--regular'],
    ];
    for (var i = 0; i < branches.length; i++) {
      var branch = wrap.querySelector(branches[i][0]);
      if (!branch) continue;
      var style = window.getComputedStyle(branch);
      if (style.display === 'none' || style.visibility === 'hidden') continue;
      var node = branch.querySelector(branches[i][1]);
      if (node) return node;
    }
    return null;
  }

  // Copies the tier price the app printed onto the theme's visible price.
  // Same rule as everywhere else: the string moves across untouched, so this
  // stays correct in every market without knowing anything about currency.
  function mirrorTierPrice() {
    var price = fromTier('.kaching-bundles__bar-price');
    if (!price) return; // widget not ready — never blank the theme's own price
    var node = visiblePriceNode();
    // Writing only on a real difference is what stops this from ping-ponging
    // with the MutationObserver that triggered it.
    if (node && node.textContent.trim() !== price) node.textContent = price;
  }

  // Everything in the ATC footer describes a bundle deal, so on a product with
  // no deal the block would be left showing whatever line carries no token —
  // "Cancel anytime" — above a switch with nothing to switch.
  //
  // The test reads the app embed's own JSON, which Shopify prints server-side
  // and which is empty when no deal applies to this product. Checking that
  // instead of the rendered widget matters: the widget paints late, so a
  // rendered-DOM test would hide the block and then flash it back in.
  function hasBundleDeal() {
    var el = document.querySelector('script.kaching-bundles-deal-block-settings');
    return !!(el && el.textContent && el.textContent.trim());
  }

  function applyDealPresence() {
    document.documentElement.classList.toggle('gemians-no-bundle-deal', !hasBundleDeal());
  }

  function sellingPlanInput() {
    return document.querySelector(CART_FORM + ' input[name="selling_plan"]');
  }

  function isSubscribed() {
    var input = sellingPlanInput();
    return !!(input && input.value);
  }

  // The widget never prints the delivery frequency, so it comes from the plan
  // the widget selected: read the id off the form, resolve the name from the
  // payload the app itself embedded. Still the app's own string, never ours.
  function planName() {
    var input = sellingPlanInput();
    if (!input || !input.value) return null; // one-time mode — nothing to say
    var script = document.querySelector('script.kaching-bundles-product');
    if (!script) return null;
    var plans;
    try {
      plans = JSON.parse(script.textContent).sellingPlans || [];
    } catch (e) {
      return null;
    }
    var id = String(input.value);
    for (var i = 0; i < plans.length; i++) {
      if (String(plans[i].id) === id) return plans[i].name || null;
    }
    return null;
  }

  applyDealPresence();

  window.Gemians.atcFooter.register({
    price: function () {
      return fromTier('.kaching-bundles__bar-price');
    },

    // Deliberately wired and deliberately null. The app does not publish a
    // shipping figure anywhere — free shipping is modelled as a progressive
    // gift, not an amount. Returning null hides the line, which is correct;
    // omitting this key would leave a dash on screen instead.
    shipping: function () {
      return null;
    },

    frequency: planName,
    interval: planName,

    isOneTime: function () {
      return !isSubscribed();
    },

    // The app keeps its free-gift block on screen in one-time mode even though
    // the gifts only apply to subscriptions, so the state is mirrored onto
    // <html> and CSS does the hiding. Deliberately a class and not a direct
    // style on the app's element: the app re-renders that block whenever a
    // tier changes, which would wipe an inline style — and re-applying it on
    // every mutation would have us fighting the app's own renderer.
    sync: function () {
      var root = document.documentElement;
      applyDealPresence();
      root.classList.toggle('gemians-one-time', !isSubscribed());

      // Per-breakpoint visibility comes from the ATC footer block's settings,
      // carried on its data attributes because the gift row is the app's
      // element and has no settings of our own to read.
      var block = document.querySelector('.gemians-atc-footer');
      if (!block) return;
      root.classList.toggle('gemians-hide-gifts-desktop', block.getAttribute('data-gemians-gifts-desktop') === 'false');
      root.classList.toggle('gemians-hide-gifts-mobile', block.getAttribute('data-gemians-gifts-mobile') === 'false');

      root.classList.toggle('gemians-hide-app-subscriptions', block.getAttribute('data-gemians-hide-app-subs') !== 'false');

      mirrorTierPrice();
    },

    onSwitch: function (oneTime) {
      var card = document.querySelector('.kaching-bundles__subscriptions__card');
      if (!card) return;
      if (isSubscribed() !== oneTime) return; // already in the requested mode
      card.click();
    },
  });
})();
